import {
  Box,
  CSSObject,
  CssBaseline,
  Divider,
  List,
  ListItem,
  Drawer as MuiDrawer,
  Theme,
  styled,
} from "@mui/material";

import { HasPermission } from "@/permissions";
import { ProtocolSetsIcon } from "@/ui-components/Icons";
import { AIDisclaimerProvider } from "@/ui-components/components/i18n/AIDisclaimerProvider";
import { MenuLink } from "@/ui-components/layouts/components/components/MenuLink/MenuLink";
import { usePrefetchCallbacks } from "@/ui-components/layouts/hooks";
import { Notifications } from "@/ui-components/notifications/Notifications";
import {
  BusinessCenterOutlined,
  ChevronLeft,
  ChevronRight,
  DashboardOutlined,
  DescriptionOutlined,
  GroupOutlined,
  InventoryOutlined,
  PlaceOutlined,
} from "@mui/icons-material";
import React, { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppBar from "../AppBar";
import { MenuPosition } from "../MenuPosition";
import { Menu, useIsMenuOpen, useSelectedMenu } from "./components/Menu";
import { PageContext } from "./context";

interface Props {
  children: ReactNode;
}

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  background: "var(--green-grey-scale-cold-tones-cold-tones-800)",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  background: "var(--green-grey-scale-cold-tones-cold-tones-800);",
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

const CenterBox = styled("div")({
  position: "fixed",
  display: "flex",
  padding: "0",
  margin: "0",
  top: "72px",
  left: "65px",
  width: "100%",
  height: "100%",
});

export const DashboardLayout = ({ children }: Props) => {
  const [menuIsOpen, setMenuIsOpen] = useIsMenuOpen();
  const [selectedMenu, _] = useSelectedMenu();
  const [title, setTitle] = useState<string>("");
  const [subtitle, setSubtitle] = useState<string>("");
  const { t } = useTranslation();

  const {
    prefetchSitesCallback,
    prefetchUsersCallback,
    prefetchCorporationsCallback,
    prefetchAssessmentsCallback,
    prefetchReportsCallback,
  } = usePrefetchCallbacks();

  const toggleMenu = React.useCallback(() => {
    setMenuIsOpen();
  }, []);

  useEffect(() => {
    setMenuIsOpen(true);
  }, [title]);

  return (
    <PageContext.Provider
      /* eslint-disable-next-line react/jsx-no-constructed-context-values */
      value={{
        title,
        subtitle,
        setTitle,
        setSubtitle,
      }}
    >
      <AIDisclaimerProvider />
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar />
        <Notifications />
        <Drawer variant="permanent" sx={{ pt: 10 }}>
          <List>
            <ListItem disablePadding sx={{ display: "block", pt: 10 }}>
              <MenuPosition
                text={menuIsOpen ? t("general.hide_menu") : t("general.show_menu")}
                onClick={toggleMenu}
                disabled={!selectedMenu}
              >
                {menuIsOpen ? <ChevronLeft /> : <ChevronRight />}
              </MenuPosition>
            </ListItem>
          </List>
          <Divider sx={{ height: "1px", bgcolor: "var(--green-grey-scale-cold-tones-cold-tones-700)" }} />
          <List>
            <ListItem disablePadding sx={{ display: "block" }}>
              <HasPermission permission={["super_admin"]}>
                <MenuLink
                  id="companies-link"
                  to="/companies"
                  label={t("general.Companies")}
                  onMouseEnter={prefetchCorporationsCallback}
                >
                  <BusinessCenterOutlined />
                </MenuLink>
              </HasPermission>
              <HasPermission permission={["super_admin", "organisation_admin"]}>
                <MenuLink id="users-link" to="/users" label={t("general.Users")} onMouseEnter={prefetchUsersCallback}>
                  <GroupOutlined />
                </MenuLink>
              </HasPermission>
              <HasPermission
                permission={[
                  "super_admin",
                  "organisation_admin",
                  "corporate_coordinator",
                  "site_coordinator",
                  "data_provider",
                  "reviewer",
                ]}
              >
                <MenuLink
                  id="business-intelligence-link"
                  to="/business-intelligence/conformity-heatmap"
                  label="Dashboard"
                >
                  <DashboardOutlined />
                </MenuLink>
              </HasPermission>
              <HasPermission
                permission={[
                  "corporate_coordinator",
                  "site_coordinator",
                  "super_admin",
                  "data_provider",
                  "reviewer",
                  "verifier",
                ]}
              >
                <MenuLink
                  id="assessments-link"
                  to="/assessments"
                  label={t("general.Assessments")}
                  onMouseEnter={prefetchAssessmentsCallback}
                >
                  <InventoryOutlined />
                </MenuLink>
              </HasPermission>
              <HasPermission
                permission={["corporate_coordinator", "site_coordinator", "super_admin", "data_provider", "reviewer"]}
              >
                <MenuLink id="sites-link" to="/sites" label={t("general.Sites")} onMouseEnter={prefetchSitesCallback}>
                  <PlaceOutlined />
                </MenuLink>
              </HasPermission>
              <HasPermission
                permission={["corporate_coordinator", "site_coordinator", "reviewer", "data_provider", "verifier"]}
              >
                <MenuLink
                  id="reports-link"
                  to="/reports"
                  label={t("general.Reports")}
                  onMouseEnter={prefetchReportsCallback}
                >
                  <DescriptionOutlined />
                </MenuLink>
              </HasPermission>
              <HasPermission permission={["data_provider", "reviewer", "corporate_coordinator", "sme_user"]}>
                <MenuLink id="protocol-link" to="/protocol_sets" label={t("general.Protocol")}>
                  <ProtocolSetsIcon />
                </MenuLink>
              </HasPermission>
            </ListItem>
          </List>
        </Drawer>
        <CenterBox>
          {menuIsOpen && (
            <Box component="div" sx={{ pb: 9, borderRight: "1px solid var(--blue-grey-scale-300);" }}>
              <Menu />
            </Box>
          )}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              pr: "60px",
              bgcolor: "#F3F6F6",
              height: "calc(100% - 72px)",
              display: "flex",
              width: "calc(100% - 251px)",
              overflowY: "auto",
            }}
          >
            {children}
          </Box>
        </CenterBox>
      </Box>
    </PageContext.Provider>
  );
};

export default DashboardLayout;
